// cSpell:words reviewee
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Dialog from '../../../components/ui/Dialog';
import { LoadingSpinner } from '../../../components/ui/LoadingSpinner';
import NotYouDialog from '../../../components/ui/NotYouDialog';
import { nominationReviewService, selfAppraisalService } from '../../../services';
import { useAuth } from '../../../hooks/useAuth';
import type { ApiNominationResponse, ApiNominator } from '../../../types';
import NominationReviewPageView from './NominationReviewPageView';
import arrowBackIcon from '../../../assets/arrow_back.svg';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';
import Tooltip from '../../../components/ui/Tooltip';
import NoActiveCycle from '../../../components/ui/NoActiveCycle';
import { useActiveCycle } from '../../../hooks/useActiveCycle';

interface Employee {
  id: string;
  employeeName: string;
  employeeId: string;
  nominationRelationshipId: number;
  designation: string;
  department: string;
  nominationAs: 'Peer' | 'Subordinate' | 'Additional Subordinate' | 'Superior' | 'Stakeholder' | 'Additional Superior';
  reportingManager?: string;
  email?: string;
  reportingManagerEmail?: string;
}

interface RatingCriterion {
  id: string;
  description: string;
  rating?: number;
  tooltip?: string;
}

const NominationReviewPage: React.FC = () => {
  const { employeeId, tab } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasActiveCycle = useActiveCycle();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [revieweeEmployeeName, setRevieweeEmployeeName] = useState<string | null>(null);
  
  // Form state for feedback
  const [appreciativeFeedback, setAppreciativeFeedback] = useState('');
  const [constructiveFeedback, setConstructiveFeedback] = useState('');
  const [sendCopyToEmail, setSendCopyToEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Deadline state
  // DEADLINE CHECK DISABLED - Always allow submissions
  const [nominationDeadlinePassed] = useState(false);
  
    // Dialog state
  const [dialogState, setDialogState] = useState({
    isOpen: false,
    type: 'info' as 'success' | 'error' | 'warning' | 'info',
    title: '',
    message: '',
    onConfirm: undefined as (() => void) | undefined
  });

  // "Not You?" dialog state
  const [notYouDialogOpen, setNotYouDialogOpen] = useState(false);

  // Reset checkbox on component mount/reload and when employeeId changes
  useEffect(() => {
    setSendCopyToEmail(false);
  }, [employeeId]); // Reset whenever employeeId changes

  // Rating criteria state
  const [criteria, setCriteria] = useState<RatingCriterion[]>([
    { 
      id: '1', 
      description: 'Solution Focused',
      tooltip: 'I prioritise customer needs through value-added solutions.'
    },
    { 
      id: '2', 
      description: 'Adapt to Win',
      tooltip: 'I learn, unlearn and re-learn to succeed.'
    },
    { 
      id: '3', 
      description: 'Courage to Act',
      tooltip: 'I speak up, challenge norms, and act with purpose and pace.'
    },
    { 
      id: '4', 
      description: 'Act for Enterprise',
      tooltip: 'I act on what\'s best for PETRONAS.'
    },
    { 
      id: '5', 
      description: 'Own It',
      tooltip: 'I am accountable to deliver outcomes that matter.'
    }
  ]);

  // Fetch employee data when route params or user context change
  useEffect(() => {
    const userEmail = user?.email;
    if (!employeeId || !userEmail) {
      return;
    }

    let isActive = true;

    const fetchEmployeeAndDraftData = async () => {
      setLoading(true);
      try {
        let employeeData: Employee | null = null;

        try {
          const employeeResponse = await nominationReviewService.getNominationReviews(userEmail);

          if (employeeResponse.status && employeeResponse.data) {
            const responseData = employeeResponse.data as unknown;

            const isNewApiStructure = (data: unknown): data is ApiNominationResponse => {
              return (
                typeof data === 'object' &&
                data !== null &&
                'nominators' in data &&
                Array.isArray((data as ApiNominationResponse).nominators)
              );
            };

            if (isNewApiStructure(responseData)) {
              const foundNominator = responseData.nominators.find(
                (nominator: ApiNominator) =>
                  nominator.nomination_relationship_id.toString() === employeeId
              );

              if (foundNominator) {
                employeeData = {
                  id: foundNominator.nominator_id.toString(),
                  employeeName: foundNominator.nominator_name,
                  employeeId: foundNominator.employee_id,
                  nominationRelationshipId: foundNominator.nomination_relationship_id,
                  designation: foundNominator.nominator_designation || 'Not specified',
                  department: foundNominator.nominator_department || 'Not specified',
                  nominationAs:
                    foundNominator.relationship_type === 'peer'
                      ? 'Peer'
                      : foundNominator.relationship_type === 'superior'
                        ? 'Superior'
                        : foundNominator.relationship_type === 'subordinate'
                          ? 'Subordinate'
                          : foundNominator.relationship_type === 'additional_subordinate'
                            ? 'Additional Subordinate'
                          : foundNominator.relationship_type === 'dotted_superior'
                            ? 'Additional Superior'
                            : 'Stakeholder',
                  reportingManager: foundNominator.nominator_manager?.manager_name || 'Not specified',
                  email: foundNominator.nominator_email,
                  reportingManagerEmail: foundNominator.nominator_manager?.manager_email
                };
              }
            } else if (Array.isArray(responseData)) {
              const foundEmployee = responseData.find(
                (emp: { employeeId: string }) => emp.employeeId === employeeId
              );

              if (foundEmployee) {
                employeeData = {
                  id: foundEmployee.id,
                  employeeName: foundEmployee.employeeName,
                  employeeId: foundEmployee.employeeId,
                  nominationRelationshipId: parseInt(employeeId, 10) || 0,
                  designation: foundEmployee.designation,
                  department: foundEmployee.department,
                  nominationAs: foundEmployee.nominationAs,
                  email: (foundEmployee as { employeeEmail?: string }).employeeEmail,
                  reportingManagerEmail: (foundEmployee as { reportingManagerEmail?: string }).reportingManagerEmail
                };
              }
            }
          }
        } catch (error) {
          console.error('Error fetching nomination list:', error);
        }

        if (!employeeData) {
          console.log('Employee not found in nomination list, using fallback data');
          employeeData = {
            id: employeeId,
            employeeName: `Employee ${employeeId}`,
            employeeId,
            nominationRelationshipId: parseInt(employeeId, 10) || 0,
            designation: 'Loading...',
            department: 'Loading...',
            nominationAs: 'Peer',
            reportingManager: 'Loading...'
          };
        }

        if (!isActive) return;

        setEmployee(employeeData);

        if (tab === 'view' && employeeData?.nominationRelationshipId) {
          try {
            const reviewResponse = await nominationReviewService.getReviewDetails(
              userEmail,
              employeeData.nominationRelationshipId.toString()
            );

            if (isActive && reviewResponse.status && reviewResponse.data) {
              setRevieweeEmployeeName(reviewResponse.data.reviewee?.name || null);
              setAppreciativeFeedback(reviewResponse.data.feedback.appreciative || '');
              setConstructiveFeedback(reviewResponse.data.feedback.constructive || '');
              setSendCopyToEmail(false);

              const competency = reviewResponse.data.competency;
              const competencyMapping: { [key: string]: string } = {
                '1': 'solution_focused',
                '2': 'adapt_to_win',
                '3': 'courage_to_act',
                '4': 'act_for_enterprise',
                '5': 'own_it'
              };

              setCriteria(prev =>
                prev.map(criterion => {
                  const competencyKey = competencyMapping[criterion.id];
                  const ratingValue = competency[competencyKey as keyof typeof competency];
                  const rating = typeof ratingValue === 'number' ? ratingValue : undefined;
                  return { ...criterion, rating };
                })
              );

              console.log('Review data loaded successfully');
            }
          } catch (error) {
            console.error('Error fetching review data:', error);
          }
        } else if (tab === 'review' && employeeData?.nominationRelationshipId) {
          try {
            const draftResponse = await nominationReviewService.getNominationReview(
              userEmail,
              employeeData.nominationRelationshipId.toString()
            );

            if (isActive && draftResponse.status && draftResponse.data) {
              const draftData = draftResponse.data;
              setAppreciativeFeedback(draftData.feedback.appreciative || '');
              setConstructiveFeedback(draftData.feedback.constructive || '');
              setSendCopyToEmail(false);

              const competency = draftData.competency;
              const competencyMapping: { [key: string]: string } = {
                '1': 'solution_focused',
                '2': 'adapt_to_win',
                '3': 'courage_to_act',
                '4': 'act_for_enterprise',
                '5': 'own_it'
              };

              setCriteria(prev =>
                prev.map(criterion => {
                  const competencyKey = competencyMapping[criterion.id];
                  const ratingValue = competency[competencyKey as keyof typeof competency];
                  const rating = typeof ratingValue === 'number' ? ratingValue : undefined;
                  return { ...criterion, rating };
                })
              );

              console.log('Draft data loaded successfully from new API format');
            }
          } catch (error) {
            console.log('No existing draft found or error loading draft:', error);
          }
        }

        if (isActive) {
          setSendCopyToEmail(false);
        }

        // Fetch nomination deadline information
        // DEADLINE CHECK DISABLED - Skipping deadline check
        if (isActive && userEmail) {
          try {
            const nominationResponse = await selfAppraisalService.getNominationsDefault(userEmail);
            if (nominationResponse?.data) {
              const nomLeft = nominationResponse.data.nomination_cycle_left;
              // setNominationDeadlinePassed(typeof nomLeft === 'number' && nomLeft < 0);
              console.debug('Nomination days left:', nomLeft, '(deadline check disabled)');
            }
          } catch (error) {
            console.debug('Failed to fetch nomination deadline:', error);
          }
        }
      } catch (fetchError) {
        console.error('Error in fetchEmployeeAndDraftData:', fetchError);
        if (isActive) {
          setEmployee({
            id: employeeId,
            employeeName: `Employee ${employeeId}`,
            employeeId,
            nominationRelationshipId: parseInt(employeeId, 10) || 0,
            designation: 'Error loading...',
            department: 'Error loading...',
            nominationAs: 'Peer',
            reportingManager: 'Error loading...'
          });
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchEmployeeAndDraftData();

    return () => {
      isActive = false;
    };
  }, [employeeId, tab, user?.email]);

  const handleBackToList = () => {
    navigate('/360-feedback');
  };

  // Dialog helper functions
  const showDialog = (type: 'success' | 'error' | 'warning' | 'info', title: string, message: string) => {
    setDialogState({
      isOpen: true,
      type,
      title,
      message,
      onConfirm: undefined
    });
  };

  const closeDialog = () => {
    // Check if this was a success dialog for submission
    const wasSuccessSubmission = dialogState.type === 'success' && dialogState.title === 'Submission Successful';
    
    setDialogState(prev => ({ ...prev, isOpen: false }));
    
    // Navigate to view mode after closing success submission dialog
    if (wasSuccessSubmission) {
      navigate(`/360-feedback/${employeeId}/view?success=true`);
    }
  };

  const handleNotYouClick = () => {
    setNotYouDialogOpen(true);
  };

  const handleNotYouSuccess = (newText: string) => {
    // Success is already handled by NotYouDialog component
    // No need for additional success dialog here
    console.log('NOT YOU submitted successfully:', newText);
  };

  // Form handlers
  const onAppreciativeFeedbackChange = (value: string) => {
    setAppreciativeFeedback(value);
  };

  const onConstructiveFeedbackChange = (value: string) => {
    setConstructiveFeedback(value);
  };

  const onSendCopyChange = (checked: boolean) => {
    setSendCopyToEmail(checked);
  };

  const handleRatingSelect = (criterionId: string, rating: number) => {
    setCriteria(prev => prev.map(criterion => 
      criterion.id === criterionId 
        ? { ...criterion, rating }
        : criterion
    ));
  };

  const onSaveAsDraft = async () => {
    if (!user?.email || !employee?.nominationRelationshipId) return;
    
    setIsSubmitting(true);
    try {
      // Transform criteria ratings to API format
      const ratings = nominationReviewService.transformRatingsToApiFormat(criteria);
      
      // Save draft using the service with email requirement flag
      const response = await nominationReviewService.saveDraft(
        user.email,
        employee.nominationRelationshipId.toString(),
        appreciativeFeedback,
        constructiveFeedback,
        ratings,
        sendCopyToEmail ? "true" : "false"
      );
      
      if (response.status) {
      
        setSendCopyToEmail(false);
        console.log('Draft saved successfully');
        // For draft saving, provide a contextually appropriate message
        // Show API message if it seems relevant to saving, otherwise use our own message
        let successMessage = 'Draft saved successfully! Your progress has been saved.';
        if (response.message) {
          // If API message contains words related to saving/updating/creating, use it
          const savingKeywords = ['saved', 'updated', 'created', 'stored', 'draft'];
          const messageContainsSavingContext = savingKeywords.some(keyword => 
            response.message?.toLowerCase().includes(keyword)
          );
          if (messageContainsSavingContext) {
            successMessage = response.message;
          } else {
            // API message doesn't seem related to saving, use our message but log API message
            console.log('API returned message:', response.message);
            successMessage = 'Draft saved successfully! Your progress has been saved.';
          }
        }
        showDialog('success', 'Draft Saved', successMessage);
      } else {
        console.error('Failed to save draft:', response.error);
        const failMessage = response.message || response.error || 'Failed to save draft. Please try again.';
        showDialog('error', 'Save Failed', failMessage);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      const isApiError = (err: unknown): err is { status_code: number; message: string } => {
        return typeof err === 'object' && err !== null && 'status_code' in err && 'message' in err;
      };
      const errorMessage = isApiError(error) && error.message
        ? error.message
        : error instanceof Error
          ? error.message
          : 'Error saving draft. Please try again.';
      showDialog('error', 'Save Error', errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Validation function to check if form is complete
  const isFormComplete = () => {
    // Check if all radio buttons are selected
    const allRatingsSelected = criteria.every(criterion => criterion.rating !== undefined);
    
    // Check if both feedback fields have valid character count
    const appreciativeFeedbackValid = nominationReviewService.isValidCharacterCount(appreciativeFeedback).isValid;
    const constructiveFeedbackValid = nominationReviewService.isValidCharacterCount(constructiveFeedback).isValid;
    
    return allRatingsSelected && appreciativeFeedbackValid && constructiveFeedbackValid;
  };

  // Validation function to check if draft can be saved (any data entered)
  const isDraftSaveable = () => {
    // Check if any radio button is selected (always valid for draft)
    const anyRatingSelected = criteria.some(criterion => criterion.rating !== undefined);
    
    // Check if any feedback text is entered AND meets minimum character requirement
    const appreciativeFeedbackEntered = appreciativeFeedback.trim().length > 0;
    const constructiveFeedbackEntered = constructiveFeedback.trim().length > 0;
    
    // If text is entered, it must meet validation (10+ characters)
    const appreciativeFeedbackValid = appreciativeFeedbackEntered 
      ? nominationReviewService.isValidCharacterCount(appreciativeFeedback).isValid 
      : true; // Empty is fine for draft
      
    const constructiveFeedbackValid = constructiveFeedbackEntered 
      ? nominationReviewService.isValidCharacterCount(constructiveFeedback).isValid 
      : true; // Empty is fine for draft
    
    // Draft is saveable if:
    // 1. Any radio is selected, OR
    // 2. Any text is entered AND that text meets validation requirements
    const hasValidData = anyRatingSelected || 
      (appreciativeFeedbackEntered && appreciativeFeedbackValid) || 
      (constructiveFeedbackEntered && constructiveFeedbackValid);
    
    return hasValidData;
  };

  const onSubmit = async () => {
    if (!user?.email || !employee?.nominationRelationshipId) return;
    
    setIsSubmitting(true);
    try {
      // Transform criteria ratings to API format
      const ratings = nominationReviewService.transformRatingsToApiFormat(criteria);
      console.log('Transformed ratings:', ratings);
      
      // Validate the form data
      const validation = nominationReviewService.validateNominationReview(
        appreciativeFeedback,
        constructiveFeedback,
        ratings
      );
      
      console.log('Validation result:', validation);
      
      if (!validation.isValid) {
        console.error('Validation errors:', validation.errors);
        showDialog('warning', 'Incomplete Form', 'Please complete all required fields before submitting.');
        return;
      }
      
      console.log('Starting API submission...');
      
      // Submit final review using the service
      const response = await nominationReviewService.submitFinalReview(
        user.email,
        employee.nominationRelationshipId.toString(),
        appreciativeFeedback,
        constructiveFeedback,
        ratings,
        sendCopyToEmail
      );
      
      console.log('API response:', response);
      
      if (response.status) {
        console.log('Review submitted successfully');
        
        // Option 3: Show success dialog first, then navigate after user clicks OK
        showDialog(
          'success', 
          'Submission Successful', 
          `Your nomination review for ${employee?.employeeName || 'the employee'} has been submitted successfully.`
        );
        
        // Note: Navigation to view mode will happen when user closes the dialog
        // We can add navigation logic in closeDialog if needed, or use a different approach
        
        // Alternative: Navigate immediately (current approach)
        // navigate(`/nomination-review/${employeeId}/view?success=true`);
      } else {
        console.error('Failed to submit review:', response.error);
        
        // Check if the error is about unsaved changes
        if (response.message && response.message.includes('unsaved changes')) {
          setDialogState({
            isOpen: true,
            type: 'warning',
            title: 'Save Draft',
            message: 'Please save your progress in drafts before submitting.',
            onConfirm: onSaveAsDraft
          });
        } else {
          showDialog('error', 'Submission Failed', 'Failed to submit review. Please try again.');
        }
      }
    } catch (error: unknown) {
      console.error('Error submitting review:', error);
      
      // Check if it's a specific API error about unsaved changes
      const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
      
      // Type guard for API error with specific properties
      const isApiError = (err: unknown): err is { status_code: number; message: string } => {
        return typeof err === 'object' && err !== null && 'status_code' in err && 'message' in err;
      };
      
      if (isApiError(error) && error.status_code === 400 && error.message.includes("unsaved changes")) {
        setDialogState({
          isOpen: true,
          type: 'warning',
          title: 'Save Draft',
          message: 'Please save your progress in drafts before submitting.',
          onConfirm: onSaveAsDraft
        });
      } else if (isApiError(error) && error.message) {
        showDialog('error', 'Submission Error', error.message);
      } else {
        showDialog('error', 'Submission Error', errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // If no employeeId in URL, show the main list view
  if (!employeeId || !tab) {
    return <NominationReviewPageView />;
  }

  if (!hasActiveCycle) {
    return <NoActiveCycle />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
   
      

      {/* Header with back button and employee info */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4  text-color-primary-800 ">
          <button
            onClick={handleBackToList}
            className="flex items-center text-base  text-primary-800  transition-colors"
          >
            <img src={arrowBackIcon} alt="Back" className="w-4 h-4 mr-2" />
            Back to Nomination Review List
          </button>
         
        </div>
      </div>

   


         {/* Success Message */}
      {(tab === 'view') && (  
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Your nomination review for {revieweeEmployeeName || employee?.employeeName || 'the employee'} has been submitted successfully.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Deadline Warning Message */}
      {nominationDeadlinePassed && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg py-4 px-6">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium text-amber-800">Nomination Review Deadline Passed</span>
          </div>
          <p className="text-amber-700 text-sm mt-1">The deadline for nomination reviews has passed. You can no longer make changes to your review.</p>
        </div>
      )}

      {/* Tab Navigation */}
      <Card>
      
        <div className="">
          <div>

            <div className="text-primary-800 text-base font-regular font-verdana mb-4 bg-custom-light-purple p-1 pl-2">Employee Information</div>
              {/* Employee Details */}
              <div className="grid grid-cols-1 gap-2 px-2 lg:grid-cols-2 lg:gap-8">
                <div className="space-y-3">
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Employee ID </span>
                    <span className="text-gray-900 text-sm">{employee?.employeeId}</span>
                  </div>
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Designation</span>
                    <span className="text-gray-900 text-sm">{employee?.designation}</span>
                  </div>
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Department</span>
                    <span className="text-gray-900 text-sm">{employee?.department}</span>
                  </div>
                
                </div>
                
                <div className="space-y-3">
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Nomination as</span>
                    <span className="text-gray-900 text-sm">{employee?.nominationAs}</span>
                  </div>
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Name of Employee</span>
                    <NameWithEmailTooltip
                      as="span"
                      className="text-gray-900 text-sm"
                      name={employee?.employeeName}
                      email={employee?.email}
                    />
                  </div>
                  <div className="flex flex-col gap-1 py-2 sm:flex-row sm:py-3">
                    <span className="text-gray-500 text-sm sm:w-40 sm:shrink-0">Reporting Manager</span>
                    <NameWithEmailTooltip
                      as="span"
                      className="text-gray-900 text-sm"
                      name={employee?.reportingManager}
                      email={employee?.reportingManagerEmail}
                    />
                  </div>

                </div>
              </div>
            </div>
        </div>
      </Card>


        {/* Rating Table */}
      <Card>
        <div className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-separate border-spacing-0 rounded-lg overflow-hidden">
              <thead className='bg-purple-800'>
                <tr className=" text-white" >
                  <th className="px-6 py-3 text-left text-base font-normal  w-16 first:rounded-tl-lg first:rounded-bl-lg">SNO.</th>
                  <th className="px-6 py-3 text-left text-base font-normal ">Description/Rating</th>
                  <th className="px-6 py-3 text-center text-base font-normal ">1-Excel</th>
                  <th className="px-6 py-3 text-center text-base font-normal ">2-Fully Met</th>
                  <th className="px-6 py-3 text-center text-base font-normal ">3-Mostly Met</th>
                  <th className="px-6 py-3 text-center text-base font-normal  last:rounded-tr-lg last:rounded-br-lg">4-Requires Action</th>
                </tr>
              </thead>
              <tbody>
                {criteria.map((criterion, index) => (
                  <tr key={criterion.id} className="bg-white hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{index + 1}.</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <div className="flex items-center space-x-2">
                        <span>{criterion.description}</span>
                        {criterion.tooltip && (
                          <Tooltip content={criterion.tooltip}>
                            <svg 
                              className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                              />
                            </svg>
                          </Tooltip>
                        )}
                      </div>
                    </td>
                    {[1, 2, 3, 4].map((rating) => (
                      <td key={rating} className="px-6 py-4 text-center">
                        <input
                          type="radio"
                          name={`rating-${criterion.id}`}
                          value={rating}
                          checked={criterion.rating === rating}
                          onChange={() => handleRatingSelect(criterion.id, rating)}
                          disabled={tab === 'view' || nominationDeadlinePassed}
                          className="w-4 h-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>

        {/* Feedback Sections */}
      <div className="space-y-6">
        {/* Appreciative Feedback */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add your Appreciative Feedback for the Employee <span className="text-gray-400">(10-10000 characters)</span>
            <span className={`ml-2 text-xs ${
              nominationReviewService.isValidCharacterCount(appreciativeFeedback).isValid 
                ? 'text-green-600' 
                : 'text-red-600'
            }`}>
              {nominationReviewService.getCharacterCount(appreciativeFeedback)} characters
            </span>
          </label>
          <textarea
            value={appreciativeFeedback}
            onChange={(e) => onAppreciativeFeedbackChange(e.target.value)}
            placeholder="Add your Input"
            rows={4}
            readOnly={tab === 'view' || nominationDeadlinePassed}
            className={`w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm placeholder-gray-400 ${
              tab === 'view' || nominationDeadlinePassed ? 'bg-gray-50 cursor-not-allowed' : ''
            }`}
          />
          {appreciativeFeedback.trim().length > 0 && !nominationReviewService.isValidCharacterCount(appreciativeFeedback).isValid && (
            <p className="text-red-600 text-xs mt-1">
              {nominationReviewService.isValidCharacterCount(appreciativeFeedback).message}
            </p>
          )}
        </div>

        {/* Constructive Feedback */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add your <strong>Constructive Feedback</strong> for the Employee <span className="text-gray-400">(10-10000 characters)</span>
            <span className={`ml-2 text-xs ${
              nominationReviewService.isValidCharacterCount(constructiveFeedback).isValid 
                ? 'text-green-600' 
                : 'text-red-600'
            }`}>
              {nominationReviewService.getCharacterCount(constructiveFeedback)} characters
            </span>
          </label>
          <textarea
            value={constructiveFeedback}
            onChange={(e) => onConstructiveFeedbackChange(e.target.value)}
            placeholder="Add your Input"
            rows={4}
            readOnly={tab === 'view' || nominationDeadlinePassed}
            className={`w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm placeholder-gray-400 ${
              tab === 'view' || nominationDeadlinePassed ? 'bg-gray-50 cursor-not-allowed' : ''
            }`}
          />
          {constructiveFeedback.trim().length > 0 && !nominationReviewService.isValidCharacterCount(constructiveFeedback).isValid && (
            <p className="text-red-600 text-xs mt-1">
              {nominationReviewService.isValidCharacterCount(constructiveFeedback).message}
            </p>
          )}
        </div>
      </div>

      {/* Email Copy Checkbox and Actions */}
      {tab === 'review' && (
        <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <input
              type="checkbox"
              id="sendCopy"
              checked={sendCopyToEmail}
              onChange={(e) => onSendCopyChange(e.target.checked)}
              disabled={nominationDeadlinePassed}
              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <label htmlFor="sendCopy" className={`min-w-0 break-words text-sm text-gray-700 ${nominationDeadlinePassed ? 'opacity-50' : ''}`}>
              Issue me a copy of my response on my email id. {user?.email || 'your.email@gentari.co.in'}{' '}
              <button 
                type="button"
                onClick={handleNotYouClick}
                disabled={nominationDeadlinePassed}
                className={`text-red-500 hover:underline focus:outline-none ${nominationDeadlinePassed ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Not you?
              </button>
            </label>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={onSaveAsDraft}
              disabled={isSubmitting || !isDraftSaveable() || nominationDeadlinePassed}
              className={`px-6 py-2 ${
                isDraftSaveable() && !isSubmitting && !nominationDeadlinePassed
                  ? 'text-purple-600 border-purple-600 hover:bg-purple-50'
                  : 'bg-purple-800   px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              title={nominationDeadlinePassed ? "Cannot save after deadline" : undefined}
            >
              Save as Draft
            </Button>
            <Button
              onClick={onSubmit}
              disabled={isSubmitting || !isFormComplete() || nominationDeadlinePassed}
              className={`px-6 py-2 ${
                isFormComplete() && !isSubmitting && !nominationDeadlinePassed
                  ? 'bg-purple-800 hover:bg-purple-700 text-white'
                  : 'bg-purple-800 hover:bg-purple-700 text-white px-6 py-2 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
              title={nominationDeadlinePassed ? "Cannot submit after deadline" : undefined}
            >
              Submit
            </Button>
          </div>
        </div>
      )}




      {/* Dialog Component */}
      <Dialog
        isOpen={dialogState.isOpen}
        onClose={closeDialog}
        type={dialogState.type}
        title={dialogState.title}
        message={dialogState.message}
        confirmText={dialogState.onConfirm ? "Save as Draft" : "OK"}
        cancelText="Cancel"
        onConfirm={dialogState.onConfirm}
        showCancel={!!dialogState.onConfirm}
      />

      {/* Not You Dialog */}
      <NotYouDialog
        isOpen={notYouDialogOpen}
        onClose={() => setNotYouDialogOpen(false)}
        currentEmail={user?.email || 'your.email@gentari.co.in'}
        onSuccess={handleNotYouSuccess}
      />
    </div>
    
  );
};

export default NominationReviewPage;
